/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useEffect, useRef } from 'react';
import { isRouteErrorResponse } from 'react-router';

import { addDatadogError } from './datadog-rum.utils';

/**
 * Report an error caught by a route ErrorBoundary to Datadog RUM (UCR-3022).
 *
 * A boundary that renders a fallback stops the error reaching `window.onerror`, so RUM — which
 * otherwise picks up uncaught errors on its own — sees nothing. Without this, the storefront's most
 * visible failures are the ones monitoring cannot see.
 *
 * **404s are deliberately not reported.** They are the most common route error on a public
 * storefront and are overwhelmingly bots probing dead URLs. Sending them would bury real failures in
 * noise — the same trap the server-side expected-404 logging fix avoids, and the reason an
 * error-rate monitor is worth having at all.
 *
 * Fail-soft in every direction: no-ops when the agent is absent, and deduped on error identity so a
 * re-render doesn't report twice.
 *
 * @param error The boundary's error. Ignored when nullish.
 * @param boundary Which boundary caught it, for filtering in Datadog.
 */
export function useReportErrorToDatadog(error: unknown, boundary: string): void {
    const reported = useRef<unknown>(undefined);

    useEffect(() => {
        if (error == null || reported.current === error) return;
        reported.current = error;

        if (isRouteErrorResponse(error) && error.status === 404) return;

        // React Router hands boundaries an ErrorResponse — a plain `{status, statusText, data}`
        // object, not an Error. Datadog records a non-Error as `Provided "{…}"` with no stack and a
        // message that changes with the payload, so every 5xx lands as its own ungroupable event and
        // an error-rate monitor has nothing to alert on. Wrap it in a real Error whose message is
        // stable across occurrences, keeping the original as `cause`.
        const payload = isRouteErrorResponse(error)
            ? Object.assign(new Error(`${error.status} ${error.statusText || 'Route Error'}`), {
                  cause: error,
              })
            : error;

        addDatadogError(payload, {
            boundary,
            ...(isRouteErrorResponse(error) && { status: error.status }),
        });
    }, [error, boundary]);
}
